import axios, { AxiosError, AxiosResponse } from 'axios';

import { simpleProjectServicePublicUrl, simpleProjectServiceUrl } from '../../config/environment';
import { ConfigurationAndRights } from '../../interfaces/ConfigurationAndRights';
import { ConfigurationCode } from '../../interfaces/ConfigurationCode';
import { ConfigurationRight } from '../../interfaces/ConfigurationRight';
import { ConfigurationAndRightsMapper } from '../../interfaces/mappers/ConfigurationAndRightsMapper';
import { ConfigurationCodeMapper } from '../../interfaces/mappers/ConfigurationCodeMapper';
import { CreateProjectDto } from '../../projects/services/projects.service';
import { BackendHttpError } from '../../services/axiosConfig';

export const getTransitionRights = async (
  configurationCodeOrId: string,
): Promise<ConfigurationRight[]> => {
  const response: AxiosResponse<ConfigurationRight[]> | AxiosError<BackendHttpError> =
    await axios.get(
      `${simpleProjectServiceUrl}/api/v1/projects/items/${configurationCodeOrId}/states`,
    );

  if (response instanceof Error) {
    return [];
  }

  return response.data;
};

export const getConfigurationRequestInfo = async (
  codeOrId: string,
): Promise<SimpleProjectServiceAPI.ConfigurationRequestDto> => {
  const url = `${simpleProjectServiceUrl}/api/v1/projects/items/${codeOrId}/request`;
  const response = await axios.get(url);
  if (response instanceof Error) {
    throw response;
  }
  const { data = null } = response || {};
  return data;
};

export const getConfigurationByCode = async (
  configurationCode: string,
): Promise<SimpleProjectServiceAPI.ConfigurationDto> => {
  const response:
    | AxiosResponse<SimpleProjectServiceAPI.ConfigurationDto>
    | AxiosError<BackendHttpError> = await axios.get(
    `${simpleProjectServiceUrl}/api/v1/projects/items/${configurationCode}`,
  );

  if (response instanceof Error) {
    return new Error(response.message);
  }

  return response.data;
};

export const getAnonymousConfigurationByCode = async (
  configurationCode: string,
): Promise<SimpleProjectServiceAPI.ConfigurationDto> => {
  const response:
    | AxiosResponse<SimpleProjectServiceAPI.ConfigurationDto>
    | AxiosError<BackendHttpError> = await axios.get(
    `${simpleProjectServicePublicUrl}/api/v1/projects/items/${configurationCode}`,
  );

  if (response instanceof Error) {
    return new Error(response.message);
  }

  return response.data;
};

export const migrateAnonymousRequests = async (): Promise<void> => {
  const response = await axios.post(
    `${simpleProjectServiceUrl}/api/v1/anonymous-configurations/requests/migrate`,
  );

  if (response instanceof Error) {
    console.error(response);
  }
};

/** @deprecated - useCreateConfigurationApi() */
export const importConfiguration = async (
  configuration: SimpleProjectServiceAPI.ConfigurationDataDto,
  name: string,
  sessionId: string,
): Promise<string | Error> => {
  const response = await axios.post<string>(
    `${simpleProjectServiceUrl}/api/v3/configurations/import?session-id=${sessionId}`,
    { name, configuration },
  );

  if (!response || response.status !== 200) {
    return new Error('IMPORT_FAILED');
  }
  return response.data;
};

export const getConfigurationAndRights = async (id: string): Promise<ConfigurationAndRights> => {
  const ret:
    | AxiosResponse<SimpleProjectServiceAPI.ConfigurationDataDto>
    | AxiosError<BackendHttpError> = await axios.get(
    `${simpleProjectServiceUrl}/api/v3/configurations/${id}`,
  );

  if (ret instanceof Error) {
    throw new Error('CONFIGURATION_CANNOT_BE_OPENED');
  }

  return ConfigurationAndRightsMapper.toDomain(ret.data);
};

export const getAnonymousConfigurationAndRights = async (
  codeOrId: string,
): Promise<ConfigurationAndRights> => {
  const ret:
    | AxiosResponse<SimpleProjectServiceAPI.ConfigurationDataDto>
    | AxiosError<BackendHttpError> = await axios.get(
    `${simpleProjectServicePublicUrl}/api/v3/configurations/${codeOrId}`,
  );

  if (ret instanceof Error) {
    throw new Error('CONFIGURATION_CANNOT_BE_OPENED');
  }

  return ConfigurationAndRightsMapper.toDomain(ret.data);
};

export const createProjectForConfiguration = async (
  configurationId: string,
  rootArticleName: string,
  projectDto?: CreateProjectDto,
): Promise<ConfigurationCode | Error> => {
  const body: SimpleProjectServiceAPI.CreateProjectRequest = {
    projectTitle: projectDto?.title || '',
    projectDescription: projectDto?.description || '',
    rootArticleName,
  };

  const response:
    | AxiosResponse<SimpleProjectServiceAPI.ConfigurationCodeDto>
    | AxiosError<BackendHttpError> = await axios.post(
    `${simpleProjectServiceUrl}/api/v3/configurations/${configurationId}/project`,
    body,
  );

  if (response instanceof Error) {
    return new Error('Unable to create a project.');
  }

  return ConfigurationCodeMapper.toDomain(response.data);
};

export const migrateAnonymous = async (): Promise<void> => {
  const ret: AxiosResponse | AxiosError<BackendHttpError> = await axios.post(
    `${simpleProjectServiceUrl}/api/v3/configurations/migrate-anonymous`,
  );

  if (ret instanceof Error) {
    throw ret;
  }
};
